/**
 * Roles & Permissions Management Page
 * 
 * Full CRUD for roles with permission management.
 * Protected by MANAGE_ROLES permission.
 * Follows UI/UX guidelines for component usage and accessibility.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { PageContainer, PageHeader, Button, Alert } from '@/components/ui';
import { useOrgStore } from '@/stores/orgStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { RolesService, type Role, type PermissionCategory } from '@/services/RolesService';
import { RoleFormModal } from '@/components/roles/RoleFormModal';
import { DeleteRoleModal } from '@/components/roles/DeleteRoleModal';
import { RolesTable } from '@/components/roles/RolesTable';
import { PERMISSION_METADATA } from '../../../server/lib/permissions/permissionRegistry';

const rolesService = new RolesService();

export function RolesAndPermissions() {
  const { activeOrgId } = useOrgStore();
  const queryClient = useQueryClient();
  const notificationStore = useNotificationStore();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);

  // Fetch roles
  const { data: roles = [], isLoading: isLoadingRoles } = useQuery({
    queryKey: ['roles', activeOrgId],
    queryFn: () => rolesService.getRoles(),
    enabled: !!activeOrgId,
  });

  // Fetch available permissions
  const { data: permissionCategories = [], isLoading: isLoadingPermissions} = useQuery({
    queryKey: ['permissions', 'available'],
    queryFn: async () => {
      // Group permissions by category from PERMISSION_METADATA
      const grouped: Record<string, PermissionCategory> = {};
      
      Object.entries(PERMISSION_METADATA).forEach(([key, meta]) => {
        if (!grouped[meta.category]) {
          grouped[meta.category] = {
            category: meta.category,
            permissions: [],
          };
        }
        grouped[meta.category].permissions.push({
          value: key,
          label: meta.label,
          description: meta.description,
        });
      });

      return Object.values(grouped).sort((a, b) => a.category.localeCompare(b.category));
    },
  });

  // Create role mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; description: string; permissions: string[] }) =>
      rolesService.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', activeOrgId] });
      setIsCreateModalOpen(false);
      notificationStore.success('Role created successfully');
    },
    onError: (error: any) => {
      notificationStore.error(error.message || 'Failed to create role');
    },
  });

  // Update role mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      rolesService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', activeOrgId] });
      setEditingRole(null);
      notificationStore.success('Role updated successfully');
    },
    onError: (error: any) => {
      notificationStore.error(error.message || 'Failed to update role');
    },
  });

  // Delete role mutation
  const deleteMutation = useMutation({
    mutationFn: (roleId: string) => rolesService.deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', activeOrgId] });
      setDeletingRole(null);
      notificationStore.success('Role deleted successfully');
    },
    onError: (error: any) => {
      notificationStore.error(error.message || 'Failed to delete role');
    },
  });

  return (
    <PageContainer>
      <PageHeader
        title="Roles & Permissions"
        description="Create custom roles and manage permissions for your team"
      >
        <Button
          size="sm"
          onClick={() => setIsCreateModalOpen(true)}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Create Role
        </Button>
      </PageHeader>

      {/* Info Alert */}
      <Alert variant="info" className="mt-6">
        <p className="text-sm">
          Assign roles to team members from the{' '}
          <a href="/organization/members" className="font-medium underline hover:no-underline">
            Members
          </a>{' '}
          page. System roles (Owner, Admin, Employee) cannot be edited or deleted.
        </p>
      </Alert>

      {/* Roles Table */}
      <div className="mt-6">
        <RolesTable
          roles={roles}
          loading={isLoadingRoles}
          onEdit={setEditingRole}
          onDelete={setDeletingRole}
          onCreate={() => setIsCreateModalOpen(true)}
        />
      </div>

      {/* Create/Edit Modal */}
      <RoleFormModal
        isOpen={isCreateModalOpen || !!editingRole}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingRole(null);
        }}
        onSubmit={async (data) => {
          try {
            if (editingRole) {
              await updateMutation.mutateAsync({ id: editingRole.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          } catch (error) {
            // Error is already handled in the mutation's onError
            throw error;
          }
        }}
        role={editingRole}
        permissionCategories={permissionCategories}
        isLoading={isLoadingPermissions}
      />

      {/* Delete Confirmation Modal */}
      <DeleteRoleModal
        isOpen={!!deletingRole}
        onClose={() => setDeletingRole(null)}
        onConfirm={async () => {
          if (deletingRole) {
            await deleteMutation.mutateAsync(deletingRole.id);
          }
        }}
        role={deletingRole}
        isDeleting={deleteMutation.isPending}
      />
    </PageContainer>
  );
}

